import { z } from 'zod';
import { EventStatusSchema } from './event';

/**
 * Dashboard-KPI-Statistiken — aggregierter Echtzeit-Zustand der Plattform.
 * Wird von `GET /api/stats/dashboard` geliefert.
 */
export const DashboardStatsSchema = z.object({
  /** Nächstes veröffentlichtes Event in der Zukunft. Null wenn kein Event geplant. */
  nextEvent: z
    .object({
      id: z.string(),
      title: z.string(),
      startsAt: z.coerce.date(),
      categoryId: z.string(),
      /** Verbleibende Tage bis zum Event-Start (aufgerundet). */
      countdownDays: z.number().int(),
    })
    .nullable(),
  /** Anzahl der Registrierungen heute und Delta zum Vortag. */
  registrationsToday: z.object({
    count: z.number().int(),
    /** Delta zum Vortag. Positiv = mehr als gestern, negativ = weniger. */
    trend: z.number().int(),
  }),
  /** Ticket-Bestand nach Status. */
  openTickets: z.object({
    total: z.number().int(),
    free: z.number().int(),
    assigned: z.number().int(),
  }),
  /** Check-in-Rate des letzten abgeschlossenen Events. Null wenn kein abgeschlossenes Event vorhanden. */
  lastEventCheckIn: z
    .object({
      eventId: z.string(),
      eventTitle: z.string(),
      /** Rate zwischen 0.0 und 1.0 */
      rate: z.number(),
      checkedIn: z.number().int(),
      total: z.number().int(),
    })
    .nullable(),
  /** Aktive User — gesamt und heute aktiv. */
  activeUsers: z.object({
    total: z.number().int(),
    todayActive: z.number().int(),
  }),
  /** Durchschnittlicher Loyalty-Score aller aktiven User. */
  loyaltyAverage: z.number(),
});

/** TypeScript Type — abgeleitet aus DashboardStatsSchema */
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

/**
 * Matchday-Statistiken für eine Veranstaltung — erweitert um ausstehende Registrierungen.
 * Wird von `GET /api/stats/matchday/:eventId` geliefert.
 */
export const MatchdayStatsSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  startsAt: z.coerce.date(),
  status: EventStatusSchema,
  totalAssigned: z.number().int(),
  totalCheckedIn: z.number().int(),
  /** Check-in-Rate in Prozent (0–100). */
  checkInRate: z.number().int(),
  /** Liste aller Check-ins. */
  checkIns: z.array(
    z.object({
      userId: z.string(),
      displayName: z.string(),
      checkedInAt: z.coerce.date(),
      method: z.string(),
    }),
  ),
  /** Registrierte User, die noch nicht eingecheckt sind. */
  pendingUsers: z.array(
    z.object({
      userId: z.string(),
      displayName: z.string(),
      email: z.string(),
      registeredAt: z.coerce.date(),
    }),
  ),
  /** Ticket-Stats pro Ticket. */
  tickets: z.array(
    z.object({
      ticketId: z.string(),
      userId: z.string().nullable(),
      displayName: z.string(),
      checkedIn: z.boolean(),
    }),
  ),
});

/** TypeScript Type — abgeleitet aus MatchdayStatsSchema */
export type MatchdayStats = z.infer<typeof MatchdayStatsSchema>;
